import styled, { css } from 'styled-components'

export interface LayerSelectStyledProps {
  $isOpen?: boolean
  $size?: 'small' | 'medium' | 'large'
  $disabled?: boolean
}

const sizeStyles = {
  small: css`
    min-height: 32px;
    font-size: ${({ theme }) => theme.typography.fontSize.small};
    padding: 6px 12px;

    .dropdown-icon {
      width: 14px;
      height: 14px;
    }
  `,
  medium: css`
    min-height: 40px;
    font-size: ${({ theme }) => theme.typography.fontSize.body};
    padding: 8px 16px;

    .dropdown-icon {
      width: 16px;
      height: 16px;
    }
  `,
  large: css`
    min-height: 48px;
    font-size: ${({ theme }) => theme.typography.fontSize.section};
    padding: 12px 20px;

    .dropdown-icon {
      width: 18px;
      height: 18px;
    }
  `,
}

export const Container = styled.div.withConfig({
  shouldForwardProp: (prop) => !prop.startsWith('$'),
})<LayerSelectStyledProps>`
  position: relative;
  display: inline-block;
  min-width: 200px;
`

export const Trigger = styled.button.withConfig({
  shouldForwardProp: (prop) => !prop.startsWith('$'),
})<LayerSelectStyledProps>`
  width: 100%;
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  color: ${({ theme }) => theme.colors.text.primary};
  font-family: ${({ theme }) => theme.typography.fontFamily.sans};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);

  ${({ $size = 'medium' }) => sizeStyles[$size]}

  &:hover:not(:disabled) {
    border-color: ${({ theme }) => theme.colors.border.focus};
    box-shadow: ${({ theme }) => theme.shadows.sm};
  }

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primary}20;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
    background: ${({ theme }) => theme.colors.background.tertiary};
  }

  ${({ $isOpen, theme }) =>
    $isOpen &&
    css`
      border-color: ${theme.colors.primary};
      box-shadow: 0 0 0 3px ${theme.colors.primary}20;

      .dropdown-icon {
        transform: rotate(180deg);
      }
    `}

  .dropdown-icon {
    stroke: currentColor;
    stroke-width: 2;
    fill: none;
    flex-shrink: 0;
    transition: transform 0.2s ease;
  }
`

export const Dropdown = styled.div.withConfig({
  shouldForwardProp: (prop) => !prop.startsWith('$'),
})<{ $isOpen: boolean }>`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 1000;
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  box-shadow: ${({ theme }) => theme.shadows.lg};
  max-height: 300px;
  overflow-y: auto;
  margin-top: 4px;

  ${({ $isOpen }) =>
    !$isOpen &&
    css`
      display: none;
    `}
`

export const LayerItem = styled.div.withConfig({
  shouldForwardProp: (prop) => !prop.startsWith('$'),
})<{ $enabled?: boolean; $disabled?: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  cursor: pointer;
  transition: background 0.2s ease;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};

  &:last-child {
    border-bottom: none;
  }

  &:hover:not([data-disabled='true']) {
    background: ${({ theme }) => theme.colors.background.secondary};
  }

  &[data-disabled='true'] {
    cursor: not-allowed;
    opacity: 0.5;
  }

  .layer-icon {
    width: 20px;
    height: 20px;
    stroke: currentColor;
    stroke-width: 2;
    fill: none;
    flex-shrink: 0;
  }

  .layer-content {
    flex: 1;
    min-width: 0;
  }

  .layer-label {
    font-size: ${({ theme }) => theme.typography.fontSize.small};
    font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
    color: ${({ theme }) => theme.colors.text.primary};
    margin: 0 0 2px 0;
  }

  .layer-description {
    font-size: ${({ theme }) => theme.typography.fontSize.micro};
    color: ${({ theme }) => theme.colors.text.secondary};
    margin: 0;
  }

  .layer-toggle {
    flex-shrink: 0;
  }
`
